import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { adminExport, adminProgress, adminUnblind, interpError } from "./interpReviewApi";
import { Button, Card, SectionTitle } from "./ui";

/*
 * Admin only (the server refuses everyone else): every reviewer's progress, the unblinded calibration results of a
 * reviewer who has submitted every case (refused before that), and the JSON export for echo-interp's
 * grader-agreement analysis. Shown only when /access says admin; never part of the reviewer's own views.
 */

const SRC = { ai_draft: "AI draft", experity: "Experity final", tie: "No preference", unknown: "?" };

export function downloadJson(obj, name) {
  const blob = new Blob([JSON.stringify(obj, null, 1)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

const AdminTab = ({ toast }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [unblind, setUnblind] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await adminProgress());
      setError(null);
    } catch (e) {
      setError(interpError(e, "Could not load progress"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showUnblind = async (userId) => {
    setBusy(true);
    try {
      setUnblind(await adminUnblind(userId));
    } catch (e) {
      toast(interpError(e, "Could not load the unblinded results"), "error");
    } finally {
      setBusy(false);
    }
  };

  const exportAll = async () => {
    setBusy(true);
    try {
      const body = await adminExport();
      downloadJson(body, `interp_review_export_${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e) {
      toast(interpError(e, "Export failed"), "error");
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Card className="p-6 text-[13px] text-red-700">{error}</Card>;
  if (!data) return <div className="flex justify-center py-16"><CircularProgress size={28} /></div>;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <SectionTitle right={<Button variant="secondary" disabled={busy} onClick={exportAll}>Download export (JSON)</Button>}>
          Reviewers
        </SectionTitle>
        <div className="px-4 pb-4 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="admin-progress">
            <thead>
              <tr className="text-left text-[#6B7280]">
                <th className="py-2 pr-3 font-medium">Login</th>
                <th className="py-2 pr-3 font-medium">Calibration</th>
                <th className="py-2 pr-3 font-medium">Echo</th>
                <th className="py-2 pr-3 font-medium">Last saved</th>
                <th className="py-2 pr-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {data.reviewers.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-[#6B7280]">No answers yet.</td></tr>
              )}
              {data.reviewers.map((r) => (
                <tr key={r.user_id} className="border-t border-[#F1F2F4]">
                  <td className="py-2 pr-3">{r.username || r.user_id}</td>
                  <td className="py-2 pr-3">{r.calibration.submitted}/{data.totals.calibration}
                    {r.calibration.drafts ? ` (+${r.calibration.drafts} draft)` : ""}</td>
                  <td className="py-2 pr-3">{r.echo.submitted}/{data.totals.echo}
                    {r.echo.drafts ? ` (+${r.echo.drafts} draft)` : ""}</td>
                  <td className="py-2 pr-3">{r.last_saved || "-"}</td>
                  <td className="py-2 pr-3 text-right">
                    {r.calibration_complete ? (
                      <Button variant="secondary" disabled={busy} onClick={() => showUnblind(r.user_id)}>Unblinded results</Button>
                    ) : (
                      <span className="text-[12px] text-[#6B7280]">Unblinding after all cases</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {unblind && (
        <Card>
          <SectionTitle>Unblinded calibration (reviewer {unblind.user_id})</SectionTitle>
          <div className="px-4 pb-4 flex flex-col gap-3 text-[13px]" data-testid="unblind">
            <div className="flex flex-wrap gap-4">
              {Object.entries(unblind.preferred).map(([k, n]) => (
                <span key={k}><b>{SRC[k] || k}</b> preferred: {n}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              {Object.entries(unblind.critical_error_cases).map(([k, n]) => (
                <span key={k}>Critical errors flagged in <b>{SRC[k]}</b>: {n}</span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-1 pr-3 font-medium">Criterion</th>
                    <th className="py-1 pr-3 font-medium">AI draft (mean)</th>
                    <th className="py-1 pr-3 font-medium">Experity (mean)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(unblind.mean_scores.ai_draft || {}).map((k) => (
                    <tr key={k} className="border-t border-[#F1F2F4]">
                      <td className="py-1 pr-3 capitalize">{k}</td>
                      <td className="py-1 pr-3">{unblind.mean_scores.ai_draft[k] ?? "-"}</td>
                      <td className="py-1 pr-3">{unblind.mean_scores.experity?.[k] ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-1 pr-3 font-medium">Case</th>
                    <th className="py-1 pr-3 font-medium">Study</th>
                    <th className="py-1 pr-3 font-medium">AI was</th>
                    <th className="py-1 pr-3 font-medium">Preferred</th>
                    <th className="py-1 pr-3 font-medium">Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {unblind.cases.map((c) => (
                    <tr key={c.item_id} className="border-t border-[#F1F2F4] align-top">
                      <td className="py-1 pr-3">{c.label}</td>
                      <td className="py-1 pr-3">{c.study_type}</td>
                      <td className="py-1 pr-3">Report {c.ai_draft?.side || "?"}</td>
                      <td className="py-1 pr-3">{SRC[c.preferred] || c.preferred}</td>
                      <td className="py-1 pr-3 whitespace-pre-wrap">{c.comment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminTab;
